import React, { useCallback, useEffect, useState } from "react"
import { Col, Row } from "react-bootstrap"
import { FormattedMessage, injectIntl } from "react-intl"
import Icon from "../../common/icon/icon.component"
import Image from "../../common/image/image.component"
import StandardList from "../../common/listing/list.component"
import NoResult from "../../common/listing/noresult.component"
import Pagination from "../../common/listing/pagination.component"
import LoadingSpinner from "../../common/spinner/loading.component"
import { ProductItemListProps } from "../../common/types/productItemList"
import { GlobalHelper } from "../../indexImports"
import { ProductItem, ProductItemDto } from "../../state/listing/product/models"
import { ProductsListingService } from "../../state/listing/product/service"
import { PRODUCT_TYPE } from "../../state/product/Types"
import SearchResults from "../../state/search/Search"
import { PaginatedSearchPayload } from "../../state/search/SearchPayload"

const ProductItemList = (props: ProductItemListProps) => {
    const [searchFilters, setSearchFilters] = useState<PaginatedSearchPayload>({
        keywords: "",
        activePage: 1,
        pageSize: 5,
        sortByField: "titleSort",
        sortAsc: false,
    })
    const [loading, setLoading] = useState<boolean>(false)
    const [totalItemsCount, setTotalItemsCount] = useState<number>(0)
    const [itemsList, setItemsList] = useState<ProductItemDto[]>([])
    const infoMessage = props.infoMessage || ""

    const getFilters = () => {
        let filters = {}
        if (Object.values(props.filters).length > 0 || props.productType.length > 0) {
            if (Object.values(props.filters).length > 0 && props.productType.length > 0) {
                filters = {
                    labelIds: props.filters,
                    type: props.productType
                }
            } else if (Object.values(props.filters).length > 0) {
                filters = {
                    labelIds: props.filters
                }
            } else if (props.productType.length > 0) {
                filters = {
                    type: props.productType
                }
            }
        }

        if (props.additionalFilters) {
            filters = {
                ...filters,
                ...props.additionalFilters
            }
        }

        return filters
    }

    const fetchListing = useCallback(async () => {
        setLoading(true)

        const res = await ProductsListingService.getProductsList({
            ...searchFilters,
            keywords: GlobalHelper.formatSearchKeywords(props.searchKeywords),
            filters: getFilters(),
        })

        if (res instanceof SearchResults && res.hasResults()) {
            setTotalItemsCount(res.metadata.totalItemsCount)
            const newProdItems = res.getData().map(item => {
                const unknownItem = item as unknown
                const newItem = new ProductItem(unknownItem as ProductItemDto)
                return newItem.getDto()
            })
            setItemsList(newProdItems)
        } else {
            setItemsList([])
        }

        setLoading(false)
    }, [props.filters, props.searchKeywords, searchFilters.activePage, searchFilters.pageSize])

    useEffect(() => {
        void fetchListing()
    }, [fetchListing])

    const handleAddItem = (item: ProductItemDto) => {
        props.addProductCallback(item)
    }

    const headers = {
        data: [
            <FormattedMessage
                key="product.components.product_item_list_image"
                id="product.components.product_item_list_image"
                defaultMessage="Image"
            />,
            <FormattedMessage
                key="product.components.product_item_list_name"
                id="product.components.product_item_list_name"
                defaultMessage="Name"
            />,
            <FormattedMessage
                key="product.components.product_item_list_price"
                id="product.components.product_item_list_price"
                defaultMessage="Price"
            />
        ]
    }

    const rows = itemsList?.map(item => {
        const image = item && item.imageIds?.length > 0 ?
            <Image key={item.id} imageId={item.imageIds[0]} size="very-small" /> :
            <Icon value={"image"} style={{ fontSize: "30px" }} />

        const title = [] as JSX.Element[]
        title.push(<p key={item.id} className="m-0">{item.title}</p>)

        const price = [] as JSX.Element[]
        const itemSelected = !!props.selectedItemIds?.find(id => id === item.id)
        const isItemMatrix = item.type === PRODUCT_TYPE.MATRIX_PARENT
        const btnOutlineClass = itemSelected ? "btn-outline-success" : "btn-outline-primary"
        price.push(
            <div className="w-100 d-flex align-items-center justify-content-between">
                <div className="w-75">
                    <p key={item.id} className="m-0">{item.defaultUnitPrice}</p>
                </div>
                <div className="w-25">
                    <button
                        title={"Add " + item.title}
                        id={item.id}
                        onClick={() => handleAddItem(item)}
                        className={"btn btn-sm btn-item-add-product padding-left-0-for-inner-span " + btnOutlineClass}
                    >
                        {isItemMatrix 
                            ? <Icon value={"chevron-right"}/>
                            : itemSelected ?
                                <Icon value={"check"}/> :
                                <Icon value={"plus-circle"}/>
                        }
                    </button>
                </div>
            </div>
        )

        return {
            data: [
                image,
                title,
                price
            ]
        }
    })

    const handlePageSizeChange = (size: number) => {
        setSearchFilters({
            ...searchFilters,
            activePage: 1,
            pageSize: size
        })
    }

    const handlePageChange = (pageNumber) => {
        setSearchFilters({
            ...searchFilters,
            activePage: pageNumber
        })
    }

    const Listing = () => {
        return (<>
            <StandardList
                smallLine={true}
                headers={headers}
                rows={rows}
                className="row pt-3"
            />
            <div className="pb-2">
                <Row>
                    <Col md={6}>
                        <Pagination
                            showPageSize={true}
                            onPageSizeChange={handlePageSizeChange}
                            activePage={searchFilters.activePage}
                            pageSize={searchFilters.pageSize}
                            totalItemsCount={totalItemsCount}
                            onChange={handlePageChange}
                        />
                    </Col>
                    <Col md={6} className="d-flex justify-content-end align-items-center">
                        <div className="d-flex">
                            {
                                infoMessage && 
                                <small className="text-primary">{infoMessage}</small>
                            }
                        </div>
                    </Col>
                </Row>
            </div>
        </>)
    }

    if (loading) {
        return <LoadingSpinner className="pt-4" />
    }

    if (itemsList.length === 0) {
        return <NoResult
            noButton={true}
            noContainer={true}
        />
    }

    return (<>
        <div className="container-fluid">
            <div className="row">
                <div className="col-12">
                    <Listing/>
                </div>
            </div>
        </div>
    </>)

}

export default injectIntl(ProductItemList)


// WEBPACK FOOTER //
// ./src/product/components/ProductItemList.tsx